import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import WizardSelect from '../../components/ui/WizardSelect';
import WizardSearchableMultiSelect from '../../components/ui/WizardSearchableMultiSelect';
import { FieldError, inpClass } from './criminalFieldUi';
import { isCaseRequestUuid } from './taskCaseUpdateHelpers';
import { setFlashToast } from '../../utils/flashToast';
import { formatDisplayDate, formatDisplayDateTime } from '../../utils/dateFormat';
import {
  cancelSubsidiaryTask,
  createCaseRequestSubsidiaryTask,
  listCaseRequestSubsidiaryTasks,
  listSubTaskAssignees,
  listSubsidiaryTaskOptions,
  listTimeUnits,
  subTaskAssigneeLabel,
  type CaseRequestSubsidiaryTaskRow,
  type CreateSubsidiaryTaskRequest,
  type SubsidiaryTaskOptionRow,
  type TimeUnitRow,
} from '../../services/caseRequestSubsidiaryTasks';
import type { TaskCaseSubTasksTabProps } from './taskCaseSubTasksTypes';

type LoadState = 'idle' | 'loading' | 'error';

function statusPillClass(status: string): string {
  const s = status.trim().toLowerCase();
  if (s.includes('complete') || s.includes('closed') || s.includes('done')) return 'pill p-com';
  if (s.includes('progress') || s.includes('review')) return 'pill p-ip';
  if (s.includes('pending')) return 'pill p-pend';
  if (s.includes('cancel')) return 'pill p-aw';
  return 'pill p-aw';
}

function toSelectOptions(items: { id: string; name: string; code?: string }[]): { id: string; label: string }[] {
  return items.map((x) => ({ id: x.id, label: x.name || x.code || x.id }));
}

function onlyDigitsMax3(value: string): string {
  const digits = value.replace(/[^\d]/g, '');
  return digits.slice(0, 3);
}

function CancelIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <line x1="15" y1="9" x2="9" y2="15" />
      <line x1="9" y1="9" x2="15" y2="15" />
    </svg>
  );
}

function defaultDaysUnitId(units: TimeUnitRow[]): string {
  const days = units.find((x) => x.active !== false && (x.code ?? '').toUpperCase() === 'DAYS');
  if (days?.id) return days.id;
  const byName = units.find((x) => x.active !== false && /day/i.test(x.name ?? ''));
  return byName?.id ?? '';
}

function toHours(value: number, unitId: string, units: TimeUnitRow[]): number | null {
  const unit = units.find((x) => x.id === unitId);
  if (!unit) return null;
  const code = (unit.code ?? '').toUpperCase();
  if (code === 'HOURS') return value;
  if (code === 'DAYS') return value * 24;
  return null;
}

export default function TaskCaseCounselSubTasksTab({
  caseRequestId,
  workflowStage,
  readOnly = false,
}: TaskCaseSubTasksTabProps) {
  const { t } = useTranslation();
  const useLiveApi = isCaseRequestUuid(caseRequestId);

  const [tasksMeta, setTasksMeta] = useState<{ loadedFor: string; errorFor: string | null }>({
    loadedFor: '',
    errorFor: null,
  });
  const [tasks, setTasks] = useState<CaseRequestSubsidiaryTaskRow[]>([]);

  const [lookupsMeta, setLookupsMeta] = useState({
    subsidiaryLoaded: false,
    subsidiaryError: false,
    timeUnitsLoaded: false,
    timeUnitsError: false,
  });

  const [subsidiaryTaskOptions, setSubsidiaryTaskOptions] = useState<SubsidiaryTaskOptionRow[]>([]);
  const [timeUnits, setTimeUnits] = useState<TimeUnitRow[]>([]);
  const [assigneeOptions, setAssigneeOptions] = useState<{ id: string; label: string }[]>([]);
  const [assigneeLabelById, setAssigneeLabelById] = useState<Record<string, string>>({});
  const [assigneesLoading, setAssigneesLoading] = useState(false);
  const [assigneesError, setAssigneesError] = useState(false);
  const assigneeFetchRef = useRef<AbortController | null>(null);

  const tasksState: LoadState = !useLiveApi
    ? 'idle'
    : tasksMeta.errorFor === caseRequestId
      ? 'error'
      : tasksMeta.loadedFor === caseRequestId
        ? 'idle'
        : 'loading';

  const subsidiaryOptionsState: LoadState = !useLiveApi
    ? 'idle'
    : lookupsMeta.subsidiaryError
      ? 'error'
      : lookupsMeta.subsidiaryLoaded
        ? 'idle'
        : 'loading';

  const timeUnitsState: LoadState = !useLiveApi
    ? 'idle'
    : lookupsMeta.timeUnitsError
      ? 'error'
      : lookupsMeta.timeUnitsLoaded
        ? 'idle'
        : 'loading';

  const assigneesState: LoadState = !useLiveApi
    ? 'idle'
    : assigneesError
      ? 'error'
      : 'idle';

  const [subsidiaryTaskId, setSubsidiaryTaskId] = useState('');
  const [description, setDescription] = useState('');
  const [assignedUserIds, setAssignedUserIds] = useState<string[]>([]);
  const [ttc, setTtc] = useState('');
  const [ttcTimeUnitId, setTtcTimeUnitId] = useState('');
  const [ttr, setTtr] = useState('');
  const [ttrTimeUnitId, setTtrTimeUnitId] = useState('');
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [cancelTaskId, setCancelTaskId] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (!useLiveApi) return;
    const controller = new AbortController();
    const signal = controller.signal;

    void listSubsidiaryTaskOptions({ signal })
      .then((res) => {
        if (signal.aborted) return;
        if (!res.success) {
          setLookupsMeta((m) => ({ ...m, subsidiaryError: true }));
          return;
        }
        setSubsidiaryTaskOptions(res.data);
        setLookupsMeta((m) => ({ ...m, subsidiaryLoaded: true, subsidiaryError: false }));
      })
      .catch(() => {
        if (!signal.aborted) setLookupsMeta((m) => ({ ...m, subsidiaryError: true }));
      });

    void listTimeUnits({ signal })
      .then((res) => {
        if (signal.aborted) return;
        if (!res.success) {
          setLookupsMeta((m) => ({ ...m, timeUnitsError: true }));
          return;
        }
        setTimeUnits(res.data);
        setLookupsMeta((m) => ({ ...m, timeUnitsLoaded: true, timeUnitsError: false }));
      })
      .catch(() => {
        if (!signal.aborted) setLookupsMeta((m) => ({ ...m, timeUnitsError: true }));
      });

    return () => controller.abort();
  }, [useLiveApi]);

  const fetchAssignees = useCallback((search: string) => {
    if (!useLiveApi) return;
    assigneeFetchRef.current?.abort();
    const controller = new AbortController();
    assigneeFetchRef.current = controller;
    setAssigneesLoading(true);

    void listSubTaskAssignees({ search, signal: controller.signal })
      .then((res) => {
        if (controller.signal.aborted) return;
        if (!res.success) {
          setAssigneesError(true);
          return;
        }
        setAssigneesError(false);
        const opts = res.data.map(subTaskAssigneeLabel);
        setAssigneeOptions(opts);
        setAssigneeLabelById((prev) => {
          const next = { ...prev };
          for (const o of opts) next[o.id] = o.label;
          return next;
        });
      })
      .catch(() => {
        if (!controller.signal.aborted) setAssigneesError(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setAssigneesLoading(false);
      });
  }, [useLiveApi]);

  useEffect(() => () => assigneeFetchRef.current?.abort(), []);

  const reloadTasks = async () => {
    if (!useLiveApi) return;
    setTasksMeta({ loadedFor: '', errorFor: null });
    const res = await listCaseRequestSubsidiaryTasks({ caseRequestId });
    if (!res.success) {
      setTasksMeta({ loadedFor: '', errorFor: caseRequestId });
      return;
    }
    setTasks(res.data.tasks);
    setTasksMeta({ loadedFor: caseRequestId, errorFor: null });
  };

  useEffect(() => {
    if (!useLiveApi) return;
    const controller = new AbortController();
    void listCaseRequestSubsidiaryTasks({ caseRequestId, signal: controller.signal })
      .then((res) => {
        if (controller.signal.aborted) return;
        if (!res.success) {
          setTasksMeta({ loadedFor: '', errorFor: caseRequestId });
          return;
        }
        setTasks(res.data.tasks);
        setTasksMeta({ loadedFor: caseRequestId, errorFor: null });
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setTasksMeta({ loadedFor: '', errorFor: caseRequestId });
        }
      });
    return () => controller.abort();
  }, [useLiveApi, caseRequestId]);

  const subsidiarySelectOptions = useMemo(
    () =>
      toSelectOptions(
        subsidiaryTaskOptions
          .filter((x) => x.active !== false)
          .map((x) => ({ id: x.id, name: x.name, code: x.code })),
      ),
    [subsidiaryTaskOptions],
  );

  const timeUnitSelectOptions = useMemo(
    () =>
      toSelectOptions(
        timeUnits
          .filter((x) => x.active !== false && ['DAYS', 'HOURS','WEEKS'].includes((x.code ?? '').toUpperCase()))
          .map((x) => ({ id: x.id, name: x.name, code: x.code })),
      ),
    [timeUnits],
  );

  const daysUnitId = useMemo(() => defaultDaysUnitId(timeUnits), [timeUnits]);
  const effectiveTtcTimeUnitId = ttcTimeUnitId || daysUnitId;
  const effectiveTtrTimeUnitId = ttrTimeUnitId || daysUnitId;

  const hasUnsavedDraft = useMemo(
    () =>
      Boolean(
        subsidiaryTaskId ||
          description.trim() ||
          assignedUserIds.length > 0 ||
          ttc.trim() ||
          ttr.trim() ||
          (effectiveTtcTimeUnitId && effectiveTtcTimeUnitId !== daysUnitId) ||
          (effectiveTtrTimeUnitId && effectiveTtrTimeUnitId !== daysUnitId),
      ),
    [subsidiaryTaskId, description, assignedUserIds, ttc, ttr, effectiveTtcTimeUnitId, effectiveTtrTimeUnitId, daysUnitId],
  );

  const handleCancelClick = () => {
    if (hasUnsavedDraft) {
      setCancelConfirmOpen(true);
      return;
    }
    resetForm();
  };

  const handleCancelSubTask = async (subsidiaryTaskId: string) => {
    if (!useLiveApi || cancelling) return;
    setCancelling(true);
    try {
      const res = await cancelSubsidiaryTask({ subsidiaryTaskId });
      if (!res.success) {
        setFlashToast({ message: res.message ?? 'Failed to cancel subsidiary task.', variant: 'error' });
        return;
      }
      setFlashToast({ message: 'Subsidiary task cancelled.', variant: 'success' });
      await reloadTasks();
    } finally {
      setCancelling(false);
      setCancelTaskId(null);
    }
  };

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!subsidiaryTaskId) next.subsidiaryTaskId = 'Subsidiary Task is required.';
    if (!assignedUserIds.length) next.assignedUserIds = 'Assign To is required.';
    // TTC and TTR are optional: only validate unit or numeric when provided
    if (ttc.trim() && !effectiveTtcTimeUnitId) next.ttcTimeUnitId = 'Time unit is required.';
    if (ttr.trim() && !effectiveTtrTimeUnitId) next.ttrTimeUnitId = 'Time unit is required.';

    const ttcNum = Number(ttc);
    const ttrNum = Number(ttr);
    if (ttc.trim() && (!Number.isFinite(ttcNum) || ttcNum <= 0)) next.ttc = 'Must be a valid positive number.';
    if (ttr.trim() && (!Number.isFinite(ttrNum) || ttrNum <= 0)) next.ttr = 'Must be a valid positive number.';
    const ttcHours = ttc.trim() ? toHours(ttcNum, effectiveTtcTimeUnitId, timeUnits) : null;
    const ttrHours = ttr.trim() ? toHours(ttrNum, effectiveTtrTimeUnitId, timeUnits) : null;
    if (
      !next.ttc &&
      !next.ttr &&
      ttcHours != null &&
      ttrHours != null &&
      Number.isFinite(ttcHours) &&
      Number.isFinite(ttrHours) &&
      ttrHours >= ttcHours
    ) {
      next.ttr = 'Should be less than Time to Complete.';
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const resetForm = () => {
    setSubsidiaryTaskId('');
    setDescription('');
    setAssignedUserIds([]);
    setTtc('');
    setTtcTimeUnitId(daysUnitId || '');
    setTtr('');
    setTtrTimeUnitId(daysUnitId || '');
    setErrors({});
  };

  const handleAssign = async () => {
    if (!useLiveApi || readOnly || saving) return;
    if (!validate()) return;

    setSaving(true);
    try {
      const body: CreateSubsidiaryTaskRequest = {
        subsidiaryTaskId,
        description: description.trim(),
        assignedUserIds,
      };
      if (ttc.trim()) {
        body.ttc = Number(ttc);
        body.ttcTimeUnitId = effectiveTtcTimeUnitId;
      }
      if (ttr.trim()) {
        body.ttr = Number(ttr);
        body.ttrTimeUnitId = effectiveTtrTimeUnitId;
      }

      const res = await createCaseRequestSubsidiaryTask({ caseRequestId, body });

      if (!res.success) {
        setFlashToast({
          message: res.message ?? 'Failed to assign subsidiary task.',
          variant: 'error',
        });
        return;
      }

      setFlashToast({
        message:
          assignedUserIds.length === 1
            ? 'Subsidiary task assigned successfully.'
            : `${assignedUserIds.length} subsidiary tasks assigned successfully.`,
        variant: 'success',
      });
      resetForm();
      await reloadTasks();
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <ConfirmDialog
        isOpen={cancelConfirmOpen}
        title="Cancel Changes"
        message="Are you sure you want to cancel? Any unsaved changes will be lost."
        onClose={() => setCancelConfirmOpen(false)}
        onConfirm={() => {
          resetForm();
          setCancelConfirmOpen(false);
        }}
      />

      <ConfirmDialog
        isOpen={cancelTaskId !== null}
        title="Cancel Subsidiary Task"
        message="Are you sure you want to cancel this subsidiary task? The assigned junior counsel will no longer be able to work on it."
        onClose={() => setCancelTaskId(null)}
        onConfirm={() => {
          if (cancelTaskId) void handleCancelSubTask(cancelTaskId);
        }}
      />

      <div className="log-title">Sub Tasks</div>

      {!useLiveApi ? (
        <p className="tcd-doc-empty-msg">Case request id is missing or invalid.</p>
      ) : (
        <>
          {!readOnly ? (
            <div className="tcd-subtask-create">
              <div className="tcd-subtask-create-title">Create Subsidiary Task</div>

              <div className="ccr-fr c2">
                <div className="ccr-fg" style={{ gridColumn: '1 / -1' }}>
                  <label>
                    Subsidiary Task <span className="req">*</span>
                  </label>
                  <WizardSelect
                    id="st-subsidiaryTask"
                    value={subsidiaryTaskId}
                    placeholder={subsidiaryOptionsState === 'error' ? 'Failed to load' : 'Select Subsidiary Task'}
                    options={subsidiarySelectOptions}
                    disabled={subsidiaryOptionsState === 'loading' || subsidiaryOptionsState === 'error'}
                    loading={subsidiaryOptionsState === 'loading'}
                    hasError={Boolean(errors.subsidiaryTaskId)}
                    emptyMessage="No data found"
                    onChange={(v) => {
                      setSubsidiaryTaskId(v);
                      setErrors((e) => ({ ...e, subsidiaryTaskId: '' }));
                    }}
                  />
                  <FieldError message={errors.subsidiaryTaskId} />
                </div>

                <div className="ccr-fg" style={{ gridColumn: '1 / -1' }}>
                  <label>Description</label>
                  <textarea
                    id="st-description"
                    className="ccr-inp"
                    rows={3}
                    maxLength={2000}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Enter detailed description of the subsidiary task..."
                  />
                </div>

                <div className="ccr-fg" style={{ gridColumn: '1 / -1' }}>
                  <label>Workflow Stage</label>
                  <input className="ccr-inp tcd-ro-field" type="text" value={workflowStage || '—'} readOnly disabled />
                </div>

                <div className="ccr-fg" style={{ gridColumn: '1 / -1' }}>
                  <label>
                    Assign To <span className="req">*</span>
                  </label>
                  <WizardSearchableMultiSelect
                    id="st-assignTo"
                    value={assignedUserIds}
                    placeholder={assigneesState === 'error' ? 'Failed to load' : 'Select User(s)'}
                    options={assigneeOptions}
                    selectedOptionLabels={assigneeLabelById}
                    disabled={assigneesState === 'error'}
                    loading={assigneesLoading}
                    hasError={Boolean(errors.assignedUserIds)}
                    emptyMessage={t('common.noUsersFound')}
                    onSearch={fetchAssignees}
                    onChange={(ids) => {
                      setAssignedUserIds(ids);
                      setErrors((e) => ({ ...e, assignedUserIds: '' }));
                    }}
                  />
                  <FieldError message={errors.assignedUserIds} />
                </div>

                <div className="ccr-fg">
                  <label>
                    Time to Complete (TTC)
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 10 }}>
                    <input
                      id="st-ttc"
                      className={inpClass(Boolean(errors.ttc))}
                      type="text"
                      inputMode="numeric"
                      pattern="\\d*"
                      maxLength={3}
                      value={ttc}
                      onChange={(e) => {
                        setTtc(onlyDigitsMax3(e.target.value));
                        setErrors((er) => ({ ...er, ttc: '' }));
                      }}
                      placeholder="e.g. 3"
                    />
                    <WizardSelect
                      id="st-ttcUnit"
                      value={effectiveTtcTimeUnitId}
                      placeholder={timeUnitsState === 'loading' ? 'Loading…' : 'Select unit'}
                      options={timeUnitSelectOptions}
                      disabled={timeUnitsState === 'loading' || timeUnitsState === 'error'}
                      loading={timeUnitsState === 'loading'}
                      hasError={Boolean(errors.ttcTimeUnitId)}
                      clearable={false}
                      onChange={(v) => {
                        setTtcTimeUnitId(v);
                        setErrors((er) => ({ ...er, ttcTimeUnitId: '' }));
                      }}
                    />
                  </div>
                  <FieldError message={errors.ttc || errors.ttcTimeUnitId} />
                </div>

                <div className="ccr-fg">
                  <label>
                    Time to Remind (TTR)
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 10 }}>
                    <input
                      id="st-ttr"
                      className={inpClass(Boolean(errors.ttr))}
                      type="text"
                      inputMode="numeric"
                      pattern="\\d*"
                      maxLength={3}
                      value={ttr}
                      onChange={(e) => {
                        setTtr(onlyDigitsMax3(e.target.value));
                        setErrors((er) => ({ ...er, ttr: '' }));
                      }}
                      placeholder="e.g. 1"
                    />
                    <WizardSelect
                      id="st-ttrUnit"
                      value={effectiveTtrTimeUnitId}
                      placeholder={timeUnitsState === 'loading' ? 'Loading…' : 'Select unit'}
                      options={timeUnitSelectOptions}
                      disabled={timeUnitsState === 'loading' || timeUnitsState === 'error'}
                      loading={timeUnitsState === 'loading'}
                      hasError={Boolean(errors.ttrTimeUnitId)}
                      clearable={false}
                      onChange={(v) => {
                        setTtrTimeUnitId(v);
                        setErrors((er) => ({ ...er, ttrTimeUnitId: '' }));
                      }}
                    />
                  </div>
                  <FieldError message={errors.ttr || errors.ttrTimeUnitId} />
                </div>
              </div>

              <div className="tcd-step-actions">
                <button type="button" className="ccr-btn-ol" onClick={handleCancelClick} disabled={saving}>
                  Cancel
                </button>
                <button type="button" className="ccr-btn-pr" onClick={() => void handleAssign()} disabled={saving}>
                  {saving ? 'Assigning…' : 'Assign'}
                </button>
              </div>
            </div>
          ) : null}

          <div className="tcd-subtask-list-title">Subsidiary Task Details</div>
          {tasksState === 'error' ? (
            <p className="tcd-update-ts" style={{ color: 'var(--red)', padding: '10px 0' }} role="alert">
              Failed to load subsidiary tasks.
            </p>
          ) : null}
          <div className="tw tcd-subtasks-tbl-wrap">
            <table className="tcd-subtasks-tbl">
              <thead>
                <tr>
                  <th>Subsidiary Task</th>
                  <th>Description</th>
                  <th>Assigned To</th>
                  <th>Due Date</th>
                  <th>Workflow Stage</th>
                  <th>Created At</th>
                  <th>Completed At / Cancelled At</th>
                  <th>Comment</th>
                  <th>Status</th>
                  {!readOnly ? <th>Actions</th> : null}
                </tr>
              </thead>
              <tbody>
                {tasksState === 'loading' ? (
                  <tr>
                    <td
                      colSpan={readOnly ? 9 : 10}
                      style={{ padding: '18px 12px', color: 'var(--t2)', textAlign: 'center' }}
                    >
                      Loading…
                    </td>
                  </tr>
                ) : tasks.length ? (
                  tasks.map((r, idx) => (
                    <tr key={`${r.subsidiaryTask}-${r.createdAt}-${idx}`}>
                      <td className="col-subtask">{r.subsidiaryTask || '—'}</td>
                      <td className="col-desc">{r.description || '—'}</td>
                      <td>{r.assignedTo || '—'}</td>
                      <td>{r.dueDate ? formatDisplayDate(r.dueDate) : '—'}</td>
                      <td>{r.workflowStage || '—'}</td>
                      <td>{r.createdAt ? formatDisplayDateTime(r.createdAt) : '—'}</td>
                      <td>
                        {r.completedAt
                          ? formatDisplayDateTime(r.completedAt)
                          : r.cancelledAt
                          ? formatDisplayDateTime(r.cancelledAt)
                          : '—'}
                      </td>
                      <td className="col-comment">{r.comment?.trim() || '—'}</td>
                      <td className="col-status">
                        <span className={statusPillClass(r.status)}>{r.status || '—'}</span>
                      </td>
                      {!readOnly ? (
                        <td>
                          {r.showCancelButton ? (
                            <button
                              type="button"
                              className="ccr-review-file-act is-danger"
                              onClick={() => setCancelTaskId(r.subsidiaryTaskId ?? null)}
                              disabled={cancelling}
                              aria-label="Cancel subsidiary task"
                              data-tip="Cancel"
                            >
                              <CancelIcon />
                            </button>
                          ) : null}
                        </td>
                      ) : null}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={readOnly ? 9 : 10}
                      style={{ padding: '18px 12px', color: 'var(--t2)', textAlign: 'center' }}
                    >
                      {t('common.noResultsFound')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}
